import { console } from './utility/console';
import { withRetry } from './utility/retry';
import { fetch } from './utility/fetch';
import { ErrorNotifier } from './error-notifier';
import { EventChunk } from './event-chunk';
import { sendPingRequest } from './ping';

type EventChunkJSON = EventChunk['toJSON'];

interface Params {
  url: string;
  data: EventChunkJSON;
  useBeacon: boolean;
  timing: string;
  intervalSeconds?: number;
  token: string;
}

const notifyError = (data: EventChunkJSON, msg: string | Error, samplingRatio: number) => {
  const error = typeof msg === 'string' ? new Error(msg) : msg;
  ErrorNotifier.notify({
    error,
    extraPayload: data,
    samplingRatio,
  });
  return error;
};

const uploadUsingSendBeacon = ({ url, data, token }: { url: string; data: EventChunkJSON; token: string }): boolean => {
  try {
    // sendBeaconで送信する場合はheaderにtokenを含むことができないのでcontextにtokenを含める
    const processedData = { ...data, context: { ...data.context, token } };
    // corsの場合sendBeaconではjson形式で送信できないため、text/plainで送信
    // TODO: keepalive fetch が CORS Preflight 対応されていないためsendBeaconで対応
    // ref. https://crbug.com/736308
    // eslint-disable-next-line compat/compat
    const flag = window.navigator.sendBeacon(url, JSON.stringify(processedData));
    if (flag) {
      return true;
    }
    const errorMsg = 'Error: Cannot send eventChunk via Beacon API';
    console.warn(errorMsg);
    notifyError(data, errorMsg, 10000);
  } catch (error: any) {
    console.warn(error);
    notifyError(data, error, 10000);
  }
  return false;
};

const uploadUsingFetch = async ({
  url,
  data,
  intervalSeconds,
  token,
}: {
  url: string;
  data: EventChunkJSON;
  intervalSeconds: number;
  token: string;
}): Promise<void> => {
  try {
    await withRetry(
      (): Promise<any> => {
        return fetch(url, {
          method: 'POST',
          body: JSON.stringify(data),
          headers: {
            'Content-Type': 'application/json',
            'X-Repro-SDK-Token': token,
          },
          mode: 'cors',
        });
      },
      { intervalSeconds }
    );
  } catch (error) {
    console.warn(error);
  }
};

export const execute = async ({
  url,
  data,
  intervalSeconds = 1,
  useBeacon = false,
  timing,
  token,
}: Params): Promise<'beacon' | 'fetch'> => {
  data.context.extras.timing = timing;

  if (window.navigator && typeof window.navigator.sendBeacon === 'function' && useBeacon) {
    const result = uploadUsingSendBeacon({ url, data, token });
    if (result) {
      return 'beacon';
    }
  }
  // beaconで送ったかfetchで送ったかを計測できるようにするため
  // event-chunkのdefaultMetadata.use_beaconのデフォルト値はtrue
  data.context.extras.use_beacon = false;
  await uploadUsingFetch({ url, data, intervalSeconds, token });

  sendPingRequest(token);

  return 'fetch';
};
